import { Suspense, useEffect, useRef, useState } from 'react';
import { NavLink, Outlet, useLocation, useNavigate, Link } from 'react-router-dom';
import { CircleHelp, LogOut, Menu, PanelLeftClose, PanelLeftOpen, WandSparkles, X } from 'lucide-react';
import { useAuth, useMember } from './AuthProvider';
import { DrawerPanel, useDrawer } from './Drawer';
import { HomeButton } from './HomeButton';
import { Logo, Mark } from './Brand';
import { PAGES, canSee, isScheduler, pageForPath, type PageDef } from './routes';
import { useIsPhone } from './useIsPhone';
import { HelpBody } from '../help/HelpDrawer';
import { Button, Chip, Spinner } from '../ui/components';
import { useOrgRealtime } from '../features/realtime/useOrgRealtime';
import { useOrg, useSettings, useVersion } from '../data/queries';
import { GenerateDialog, useGenerateDraft } from '../features/generate/GenerateDialog';
import { PublishButton } from '../features/publish/PublishButton';
import { InputProblemsChip } from '../features/validation/InputProblems';
import { useProblems } from '../features/validation/useLiveChecks';

/**
 * The shell (spec 5.1, V2 D80). Schedulers get a sidebar (a slide-over on
 * phones) and the working top bar. Viewers get a single plain header: they
 * have one page, so there is nothing to navigate.
 */
export function Layout() {
  const { member, org, role } = useMember();
  const { signOut, session } = useAuth();
  useOrgRealtime(org.id, session?.user.id ?? null);
  const location = useLocation();
  const navigate = useNavigate();
  const drawer = useDrawer();
  const [collapsed, setCollapsed] = useState(() => {
    try { return localStorage.getItem('sidebar') === 'collapsed'; } catch { return false; }
  });
  const [mobileOpen, setMobileOpen] = useState(false);
  const page = pageForPath(location.pathname);
  const pages = PAGES.filter((p) => canSee(role, p));
  const scheduler = isScheduler(role);
  // On a phone the scheduler module is My schedule only, in the plain pilot shell (D85).
  const phone = useIsPhone();

  useEffect(() => setMobileOpen(false), [location.pathname]);

  const openHelp = (p: PageDef | undefined) => {
    if (!p) return;
    drawer.open({ title: p.label, subtitle: 'What this page is for and what you enter', body: <HelpBody page={p} /> });
  };

  // Keyboard shortcuts (spec 5.13): ? help, g + letter to jump. Esc is handled by the drawer.
  const chord = useRef<number | null>(null);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const t = e.target as HTMLElement | null;
      if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.tagName === 'SELECT' || t.isContentEditable)) return;
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      if (e.key === '?') {
        e.preventDefault();
        openHelp(page);
        return;
      }
      if (chord.current !== null) {
        window.clearTimeout(chord.current);
        chord.current = null;
        const target = pages.find((p) => p.key === e.key.toLowerCase());
        if (target) {
          e.preventDefault();
          navigate(target.path);
        }
        return;
      }
      if (e.key === 'g') {
        chord.current = window.setTimeout(() => (chord.current = null), 1200);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page?.id, pages.length]);

  const toggleSidebar = () => {
    setCollapsed((c) => {
      try { localStorage.setItem('sidebar', c ? 'expanded' : 'collapsed'); } catch { /* ignore */ }
      return !c;
    });
  };

  const main = (
    <main className="min-w-0 flex-1 overflow-y-auto px-4 py-4 md:px-6 md:py-5 print:overflow-visible print:p-0">
      <Suspense fallback={<Spinner label="Loading page" />}>
        <Outlet />
      </Suspense>
    </main>
  );

  if (!scheduler || phone) {
    return (
      <div className="flex h-full flex-col">
        <ViewerBar onHelp={() => openHelp(page)} />
        <div className="flex min-h-0 flex-1">
          {main}
          <div className="print:hidden"><DrawerPanel /></div>
        </div>
      </div>
    );
  }

  const nav = (compact: boolean, onNavigate?: () => void) => (
    <>
      <div className="flex h-14 items-center gap-2 border-b border-white/10 px-3" title={org.name}>
        {compact ? <Mark /> : (
          <div className="min-w-0 flex-1 leading-tight">
            <Logo onNavy className="h-6" />
            <div className="mt-0.5 truncate text-[11px] uppercase tracking-[0.12em] text-white/70">Pilot Scheduler</div>
          </div>
        )}
        {onNavigate && (
          <button type="button" onClick={onNavigate} className="rounded p-1 text-white/70 hover:bg-white/10 hover:text-white" aria-label="Close menu"><X size={18} /></button>
        )}
      </div>
      <ul className="flex-1 space-y-0.5 px-2 py-3">
        {pages.map((p) => (
          <li key={p.id}>
            <NavLink
              to={p.path}
              onClick={onNavigate}
              title={compact ? p.label : undefined}
              className={({ isActive }) =>
                `flex items-center gap-3 rounded-md px-2.5 py-2 text-sm ${
                  isActive ? 'bg-white/15 font-semibold text-white shadow-[inset_3px_0_0_0_#f0a63c]' : 'text-white/80 hover:bg-white/10 hover:text-white'
                }`
              }
            >
              <p.icon size={18} className="shrink-0" />
              {!compact && <span className="truncate">{p.label}</span>}
            </NavLink>
          </li>
        ))}
      </ul>
      <div className="space-y-0.5 border-t border-white/10 px-2 py-3">
        <button
          type="button"
          onClick={() => { openHelp(page); onNavigate?.(); }}
          className="flex w-full items-center gap-3 rounded-md px-2.5 py-2 text-sm text-white/80 hover:bg-white/10 hover:text-white"
          title="Help for this page (?)"
        >
          <CircleHelp size={18} className="shrink-0" />
          {!compact && <span>Help</span>}
        </button>
        <div className={`flex items-center gap-3 rounded-md px-2.5 py-2 text-sm ${compact ? 'justify-center' : ''}`} title={member.email}>
          <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-white/20 text-[11px] font-semibold uppercase">
            {member.email.charAt(0)}
          </span>
          {!compact && (
            <span className="min-w-0 flex-1 leading-tight">
              <span className="block truncate text-white/90">{member.email}</span>
              <span className="block text-[11px] capitalize text-white/60">{member.role}</span>
            </span>
          )}
          {!compact && (
            <button type="button" onClick={() => void signOut()} className="rounded p-1 text-white/70 hover:bg-white/10 hover:text-white" title="Sign out">
              <LogOut size={16} />
            </button>
          )}
        </div>
        {!onNavigate && (
          <button
            type="button"
            onClick={toggleSidebar}
            className="flex w-full items-center gap-3 rounded-md px-2.5 py-2 text-sm text-white/60 hover:bg-white/10 hover:text-white"
            title={compact ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            {compact ? <PanelLeftOpen size={18} /> : <PanelLeftClose size={18} />}
            {!compact && <span>Collapse</span>}
          </button>
        )}
      </div>
    </>
  );

  return (
    <div className="flex h-full">
      <nav aria-label="Main" className={`hidden shrink-0 flex-col bg-navy text-white transition-[width] md:flex print:hidden ${collapsed ? 'w-14' : 'w-56'}`}>
        {nav(collapsed)}
      </nav>
      {mobileOpen && (
        <>
          {/* Stacking (ui/dialog.tsx): the phone menu sits at 40/50, under modals. */}
          <div className="fixed inset-0 z-40 bg-ink/30 md:hidden" onClick={() => setMobileOpen(false)} />
          <nav aria-label="Main" className="fixed inset-y-0 left-0 z-50 flex w-64 flex-col bg-navy text-white shadow-xl md:hidden">
            {nav(false, () => setMobileOpen(false))}
          </nav>
        </>
      )}

      <div className="flex min-w-0 flex-1 flex-col">
        <TopBar onMenu={() => setMobileOpen(true)} />
        <div className="flex min-h-0 flex-1">
          {main}
          <div className="print:hidden"><DrawerPanel /></div>
        </div>
      </div>
    </div>
  );
}

function TopBar({ onMenu }: { onMenu(): void }) {
  const { org: orgInitial } = useMember();
  const settings = useSettings(orgInitial.id);
  const { problems } = useProblems(orgInitial.id);
  const gen = useGenerateDraft(orgInitial.id);
  const [genOpen, setGenOpen] = useState(false);

  const reportStart = settings.data?.report_start ?? null;
  const blocked = !reportStart ? 'Set the report start date in Settings first' : problems.errors > 0 ? 'Fix the input errors first' : null;

  const startGenerate = () => {
    setGenOpen(true);
    gen.mutate();
  };
  const closeGenerate = (open: boolean) => {
    setGenOpen(open);
    if (!open) gen.reset();
  };

  return (
    <header className="flex h-14 shrink-0 items-center gap-2 border-b-2 border-gold bg-white px-3 md:gap-3 md:px-5 print:hidden">
      <button type="button" onClick={onMenu} className="rounded p-1.5 text-ink hover:bg-band md:hidden" aria-label="Open menu"><Menu size={20} /></button>
      <HomeButton />
      <InputProblemsChip orgId={orgInitial.id} />
      <div className="flex-1" />
      <Button variant="primary" onClick={startGenerate} disabled={!!blocked || gen.isPending} busy={gen.isPending} title={blocked ?? 'Validate inputs and generate a new draft'}>
        <WandSparkles size={16} /> <span className="hidden sm:inline">Generate Draft</span><span className="sm:hidden">Generate</span>
      </Button>
      <PublishButton />
      <GenerateDialog open={genOpen} onOpenChange={closeGenerate} step={gen.step} outcome={gen.data} error={gen.error} pending={gen.isPending} />
    </header>
  );
}

/** Viewers (D79): one page, so one plain bar. */
function ViewerBar({ onHelp }: { onHelp(): void }) {
  const { member, org: orgInitial } = useMember();
  const { signOut } = useAuth();
  const org = useOrg(orgInitial.id, orgInitial);
  const version = useVersion(org.data?.current_version_id ?? null);
  return (
    <header className="flex h-14 shrink-0 items-center gap-3 border-b-2 border-gold bg-navy px-4 text-white print:hidden">
      <HomeButton dark />
      <Link to="/" className="flex min-w-0 items-center gap-3" title={orgInitial.name}>
        <Logo onNavy className="h-6 sm:h-7" />
        <span className="hidden border-l border-white/30 pl-3 text-xs uppercase tracking-[0.14em] text-white/80 sm:inline">My schedule</span>
      </Link>
      <div className="flex-1" />
      {version.data && <Chip tone="gold">v{version.data.version_no}</Chip>}
      <button type="button" onClick={onHelp} className="rounded p-1.5 text-white/80 hover:bg-white/10 hover:text-white" title="Help (?)" aria-label="Help"><CircleHelp size={18} /></button>
      <button type="button" onClick={() => void signOut()} className="flex items-center gap-1.5 rounded p-1.5 text-white/80 hover:bg-white/10 hover:text-white" title={`Sign out ${member.email}`}>
        <LogOut size={16} /><span className="hidden text-sm sm:inline">Sign out</span>
      </button>
    </header>
  );
}
